/**
 * NotificationDrawer - Sprint 19b
 *
 * Slide-out drawer showing notifications with infinite scroll.
 * @see US-19b.07
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, Loader2, Bell } from 'lucide-react';
import { Drawer } from '@/components/ui';
import { Button } from '@/components/ui';
import { NotificationItem } from './NotificationItem';
import {
  useInfiniteNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useUnreadCount,
} from '../hooks/useNotifications';
import { computeNotificationDeepLink } from '../utils/deep-link';

import type { Notification } from '@bstock/shared';

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteNotifications(15);
  const { data: unreadData } = useUnreadCount();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  // Flatten all pages into a single array
  const notifications = data?.pages.flatMap((page) => page.data) ?? [];
  const unreadCount = unreadData?.count ?? 0;

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  /**
   * Handle notification click - mark as read and navigate
   */
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }

    // Compute deep link from entityType + entityId (frontend owns routing)
    const deepLink = computeNotificationDeepLink(notification.entityType, notification.entityId);
    if (deepLink) {
      onClose();
      navigate(deepLink);
    }
  };

  /**
   * Handle mark all as read
   */
  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title="Notifications"
      description={
        unreadCount > 0
          ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
          : 'Toutes les notifications sont lues'
      }
      size="default"
    >
      {/* Header actions */}
      {unreadCount > 0 && (
        <div className="mb-4 -mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllAsRead}
            disabled={markAllAsRead.isPending}
            className="text-sm"
          >
            {markAllAsRead.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCheck className="w-4 h-4 mr-2" />
            )}
            Tout marquer comme lu
          </Button>
        </div>
      )}

      {/* Notifications list */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
            <Bell className="w-12 h-12 mb-3" />
            <p className="text-sm font-medium">Aucune notification</p>
            <p className="text-xs">Vous etes a jour !</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
              />
            ))}

            {/* Load more trigger */}
            <div ref={loadMoreRef} className="py-2">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
